import React from 'react';

import { colorizeSequenceWithSpaceMonospace, splitAndAnnotateCumulativeColoredSpaced } from '../../utils/stringUtils'; 
import './Output.css';

function Output({ analysisData }) {
    // analysisData がnullまたはundefinedの場合のハンドリング
    if (!analysisData) {
        return <p className="info">No analysis results to display yet. Please run an analysis first.</p>;
    }

    const indelData = analysisData.indel_json;
    const allData = analysisData.all_json;
    const contribsData = analysisData.contribs_json;
    const discordPlotBase64 = analysisData.discord_plot_base64;
    const editingOutcomesPlotBase64 = analysisData.editing_outcomes_plot_base64;

    // データが不完全な場合のチェック (Lambdaが全てのJSONと画像を返すことを前提)
    if (!indelData || !allData || !contribsData || !discordPlotBase64 || !editingOutcomesPlotBase64) {
        return <p className="error">Incomplete analysis results received. Please try again.</p>;
    }

    // Sequence alignment のデータ準備
    const controlChunksFinalDisplay = allData.control ? splitAndAnnotateCumulativeColoredSpaced(allData.control) : [];
    const editedChunksFinalDisplay = allData.edited ? splitAndAnnotateCumulativeColoredSpaced(allData.edited) : [];

    return (
        <div className="output-display">
            <h3>Output</h3>

            {indelData.warnings && indelData.warnings.length > 0 && (
                <div className="warning" role="alert">
                    <strong>Analysis warnings</strong>
                    <ul>
                        {indelData.warnings.map((message, index) => (
                            <li key={index}>{message}</li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="expander">
                <details open>
                    <summary>Figures</summary>
                    <div className="figure-section">
                        <div className="figure-item">
                            <h4>Discord plot</h4>
                            <img src={`data:image/png;base64,${discordPlotBase64}`} alt="Discord Plot" />
                        </div>
                        <div className="figure-item">
                            <h4>Editing outcomes</h4>
                            <img src={`data:image/png;base64,${editingOutcomesPlotBase64}`} alt="Editing Outcomes Plot" />
                        </div>
                    </div>
                </details>
            </div>

            <div className="expander">
                <details>
                    <summary>Editing efficiency</summary>
                    <table>
                        <thead>
                            <tr>
                                <th>editing_eff</th>
                                <th>BIC</th>
                                <th>r_sq</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>{indelData.editing_eff}</td>
                                <td>{indelData.BIC}</td>
                                <td>{indelData.r_sq}</td>
                            </tr>
                        </tbody>
                    </table>
                </details>
            </div>

            <div className="expander">
                <details>
                    <summary>Sequence alignment</summary>
                    {controlChunksFinalDisplay.map((controlChunk, index) => (
                        <React.Fragment key={index}>
                            <div className="mono-text">control：<span dangerouslySetInnerHTML={{ __html: controlChunk }}></span></div>
                            <div className="mono-text">edited ：<span dangerouslySetInnerHTML={{ __html: editedChunksFinalDisplay[index] }}></span></div>
                        </React.Fragment>
                    ))}
                </details>
            </div>

            <div className="expander">
                <details>
                    <summary>Allele contribution(normalized)</summary>
                    <table>
                        <thead>
                            <tr>
                                <th>Contribution</th>
                                <th>Mutation</th>
                                <th>Sequence</th>
                            </tr>
                        </thead>
                        <tbody>
                            {contribsData.contribs_list.map((entry, index) => {
                                const detailsStr = entry.indel.details.map(d => `${d.value}[${d.label}]`).join(', ');
                                const humanReadableColored = colorizeSequenceWithSpaceMonospace(entry.human_readable);
                                return (
                                    <tr key={index}>
                                        <td>{entry.rel_abundance}</td>
                                        <td>{detailsStr}</td>
                                        <td dangerouslySetInnerHTML={{ __html: humanReadableColored }}></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </details>
            </div>
        </div>
    );
}

export default Output;
